import { Card, Grid, InputLabel, Autocomplete, TextField, Switch, Icon, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useEffect, useState } from "react";
import { get, post } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";

export default function FilaProcessamento() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [regras, setRegras] = useState([]);
    const [operadores, setOperadores] = useState([]);
    const [selectedRegra, setSelectedRegra] = useState(null);
    const [selectedOperadores, setSelectedOperadores] = useState([]);
    const [selectedOperadorAusente, setSelectedOperadorAusente] = useState(null);
    const [ignorarPropriedade, setIgnorarPropriedade] = useState(false);
    const [limparFila, setLimparFila] = useState(true);
    const [respeitarAgendamento, setRespeitarAgendamento] = useState(true);

    const [loading, setLoading] = useState(false);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");
    const [resultados, setResultados] = useState([]);

    useEffect(() => {
        // Carrega as Regras ativas
        get("/regra-fila/list-all").then((res) => {
            if (res) {
                setRegras(res.filter(r => r.ativo));
            }
        });

        // Carrega Operadores da Equipe
        get("/users/list-all-operators").then((res) => {
            if (res) {
                setOperadores(res.list || res.data || (Array.isArray(res) ? res : []));
            }
        });
    }, []);

    const handleProcessar = () => {
        if (!selectedRegra) {
            setSbMessage("Selecione uma regra para processamento.");
            setErrorSB(true);
            return;
        }

        if (!selectedOperadores || selectedOperadores.length === 0) {
            setSbMessage("Selecione pelo menos um operador para receber os casos.");
            setErrorSB(true);
            return;
        }

        setLoading(true);

        const payload = {
            regraId: selectedRegra.id,
            userIds: selectedOperadores,
            operadorAusenteId: selectedOperadorAusente ? selectedOperadorAusente.id : null,
            ignorarPropriedade: ignorarPropriedade,
            limparFila: limparFila,
            respeitarAgendamento: respeitarAgendamento
        };

        post("/regra-fila/processar", payload)
            .then((res) => {
                if (res && Array.isArray(res)) {
                    setResultados(res);
                    setSbMessage("Processamento concluído com sucesso!");
                } else {
                    setResultados([]);
                    setSbMessage("Nenhum caso encontrado para os operadores selecionados.");
                }
                
                setSuccessSB(true);
                setSelectedRegra(null);
                setSelectedOperadores([]);
                setSelectedOperadorAusente(null);
            })
            .catch(() => {
                setSbMessage("Erro ao processar fila.");
                setErrorSB(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card variant="outlined" sx={{ p: 3, mb: 3, borderRadius: "12px", background: darkMode ? "#4B4F5C" : "#ffffff", borderColor: darkMode ? "#333" : "#e0e0e0" }}>
                    <Grid container spacing={3} alignItems="flex-start">
                        {/* Coluna 1: Campos de Seleção */}
                        <Grid item xs={12} md={6}>
                            <MDBox display="flex" flexDirection="column" gap={2.5}>
                                <MDBox>
                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                                        Regra de Ordenação
                                    </MDTypography>
                                    <Autocomplete
                                        options={regras}
                                        getOptionLabel={(option) => option.nome || ""}
                                        value={selectedRegra}
                                        onChange={(event, newValue) => setSelectedRegra(newValue)}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="standard" placeholder="Selecione a Regra..." />
                                        )}
                                        fullWidth
                                    />
                                </MDBox>

                                <MDBox>
                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                                        Operadores Destino (Receberão os casos na fila)
                                    </MDTypography>
                                    <Autocomplete
                                        multiple
                                        options={operadores}
                                        getOptionLabel={(option) => option.nome || option.ccNome || option.id || ""}
                                        value={operadores.filter(op => selectedOperadores.includes(op.id))}
                                        onChange={(event, newValue) => {
                                            setSelectedOperadores(newValue ? newValue.map(v => v.id) : []);
                                        }}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        renderInput={(params) => (
                                            <MDInput {...params} variant="standard" placeholder="Selecione os operadores..." />
                                        )}
                                        fullWidth
                                        noOptionsText="Nenhum operador encontrado"
                                    />
                                </MDBox>

                                <MDBox>
                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                                        Operador Ausente / Faltou (Distribuição por Ausência - Opcional)
                                    </MDTypography>
                                    <Autocomplete
                                        options={operadores}
                                        getOptionLabel={(option) => option.nome || option.ccNome || option.id || ""}
                                        value={selectedOperadorAusente}
                                        onChange={(event, newValue) => setSelectedOperadorAusente(newValue)}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        renderInput={(params) => (
                                            <MDInput {...params} variant="standard" placeholder="Selecione o operador ausente (opcional)..." />
                                        )}
                                        fullWidth
                                        noOptionsText="Nenhum operador encontrado"
                                    />
                                    <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary" }}>
                                        Processa e distribui a fila dos operadores selecionados e redistribui simultaneamente os casos do operador ausente.
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </Grid>

                        {/* Coluna 2: Opções com Switches */}
                        <Grid item xs={12} md={6}>
                            <MDBox 
                                p={2} 
                                sx={{ 
                                    backgroundColor: darkMode ? "#4B4F5C" : "#f8f9fa", 
                                    borderRadius: "10px", 
                                    border: "1px solid", 
                                    borderColor: darkMode ? "#333" : "#e9ecef",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2
                                }}
                            >
                                <MDBox display="flex" alignItems="flex-start">
                                    <Switch 
                                        checked={ignorarPropriedade}
                                        onChange={(e) => setIgnorarPropriedade(e.target.checked)}
                                        color="info"
                                        sx={{ mt: -0.5 }}
                                    />
                                    <MDBox ml={1} onClick={() => setIgnorarPropriedade(!ignorarPropriedade)} sx={{ cursor: "pointer", userSelect: "none" }}>
                                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"} display="block">
                                            Não olhar a propriedade
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary" }}>
                                            Distribuir aleatoriamente entre os operadores selecionados
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <MDBox display="flex" alignItems="flex-start">
                                    <Switch 
                                        checked={limparFila}
                                        onChange={(e) => setLimparFila(e.target.checked)}
                                        color="info"
                                        sx={{ mt: -0.5 }}
                                    />
                                    <MDBox ml={1} onClick={() => setLimparFila(!limparFila)} sx={{ cursor: "pointer", userSelect: "none" }}>
                                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"} display="block">
                                            Limpar fila atual antes de processar
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary" }}>
                                            Se desmarcado, as novas fichas são apenas acrescentadas e reordenadas na fila atual
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <MDBox display="flex" alignItems="flex-start">
                                    <Switch 
                                        checked={respeitarAgendamento}
                                        onChange={(e) => setRespeitarAgendamento(e.target.checked)}
                                        color="info"
                                        sx={{ mt: -0.5 }}
                                    />
                                    <MDBox ml={1} onClick={() => setRespeitarAgendamento(!respeitarAgendamento)} sx={{ cursor: "pointer", userSelect: "none" }}>
                                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"} display="block">
                                            Respeitar agendamento futuro
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary" }}>
                                            Não distribuir casos no dia que estão agendados para datas futuras (posteriores a hoje)
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>

                            {/* Botão de Ação */}
                            <Grid item xs={12} display="flex" justifyContent="flex-end" mt={3}>
                                <MDButton 
                                    variant="gradient" 
                                    color="info" 
                                    size="medium" 
                                    onClick={handleProcessar}
                                    disabled={loading}
                                    sx={{ px: 3 }}
                                >
                                    {loading ? (
                                        <CircularProgress size={20} color="white" />
                                    ) : (
                                        <>
                                            <Icon sx={{ mr: 1 }}>settings_applications</Icon>
                                            Processar Fila
                                        </>
                                    )}
                                </MDButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Card>

                {/* Cards de Resultado */}
                {resultados && resultados.length > 0 && (
                    <Card variant="outlined" sx={{ p: 3, borderRadius: "12px", background: darkMode ? "#4B4F5C" : "#ffffff", borderColor: darkMode ? "#333" : "#e0e0e0" }}>
                        <MDBox mb={2}>
                            <MDTypography variant="h6" fontWeight="medium" color="info">
                                Resultado da Distribuição
                            </MDTypography>
                        </MDBox>
                        <Grid container spacing={2}>
                            {resultados.map((r, idx) => (
                                <Grid item xs={12} md={6} lg={4} key={idx}>
                                    <MDBox p={2} border="1px solid" borderColor={darkMode ? "#444" : "#e0e0e0"} borderRadius="10px" bgcolor={darkMode ? "#4B4F5C" : "#f8f9fa"}>
                                        <MDTypography variant="button" color="info" fontWeight="bold">
                                            {r.operador}
                                        </MDTypography>
                                        <MDBox display="flex" justifyContent="space-between" mt={1.5}>
                                            <MDTypography variant="caption" color="text">Fila Anterior:</MDTypography>
                                            <MDTypography variant="button" fontWeight="medium">{r.antes}</MDTypography>
                                        </MDBox>
                                        <MDBox display="flex" justifyContent="space-between">
                                            <MDTypography variant="caption" color="text">Fichas Retiradas:</MDTypography>
                                            <MDTypography variant="button" fontWeight="bold" color="error">-{r.retirados || 0}</MDTypography>
                                        </MDBox>
                                        <MDBox display="flex" justifyContent="space-between">
                                            <MDTypography variant="caption" color="text">Fichas Adicionadas:</MDTypography>
                                            <MDTypography variant="button" fontWeight="bold" color="success">+{r.adicionados}</MDTypography>
                                        </MDBox>

                                        {/* Detalhamento por Status da Ficha */}
                                        {r.detalhesPorStatus && Object.keys(r.detalhesPorStatus).length > 0 && (
                                            <MDBox mt={1.5} pt={1.5} borderTop="1px dashed" borderColor={darkMode ? "#666" : "#d0d0d0"}>
                                                <MDTypography variant="caption" fontWeight="bold" color="info" display="block" mb={1}>
                                                    Detalhamento por Status:
                                                </MDTypography>
                                                <MDBox display="flex" flexDirection="column" gap={0.5}>
                                                    {Object.entries(r.detalhesPorStatus).map(([statusNome, qtd]) => (
                                                        <MDBox key={statusNome} display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.3} borderRadius="4px" bgcolor={darkMode ? "#3D414D" : "#edf2f7"}>
                                                            <MDTypography variant="caption" color="text" fontWeight="regular">
                                                                {statusNome}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" fontWeight="bold" color="info">
                                                                {qtd}
                                                            </MDTypography>
                                                        </MDBox>
                                                    ))}
                                                </MDBox>
                                            </MDBox>
                                        )}

                                        <MDBox display="flex" justifyContent="space-between" mt={1.5} pt={1} borderTop="1px solid" borderColor={darkMode ? "#333" : "#e0e0e0"}>
                                            <MDTypography variant="button" color="text" fontWeight="bold">Total Geral na Fila:</MDTypography>
                                            <MDTypography variant="button" fontWeight="bold" color="info">{r.depois}</MDTypography>
                                        </MDBox>
                                    </MDBox>
                                </Grid>
                            ))}
                        </Grid>
                    </Card>
                )}
            </Grid>

            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Erro"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />
        </Grid>
    );
}
